/**
 * Trading status / resumption banner.
 *
 * Driven entirely by `share_info.trading_status` from GET /api/share-info — the
 * copy is never hardcoded here. The banner is suppressed when the status reads as
 * ordinary trading, and it can be dismissed for the rest of the browser session
 * (a change in status brings it back, because the dismissal is keyed on the text).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BellRing, X } from 'lucide-react';
import { getCached } from '../lib/api';
import { useContent } from '../i18n/LocaleProvider';

const DISMISS_KEY = 'usp:trading-status-dismissed';
// Anything that reads as ordinary trading is not news, so it is not announced.
const ORDINARY = /^(normal|active|trading|listed|quoted)$/i;

function readDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export default function TradingStatusBanner() {
  const { UI } = useContent();
  const copy = UI.trading;
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(null);

  useEffect(() => {
    let live = true;
    setDismissed(readDismissed());
    getCached('/share-info')
      .then((data) => {
        if (!live) return;
        const value = (data?.trading_status || '').trim();
        setStatus(value && !ORDINARY.test(value) ? value : null);
      })
      .catch(() => setStatus(null));
    return () => {
      live = false;
    };
  }, []);

  if (!status || dismissed === status) return null;

  const isResumption = /resum/i.test(status) && !/pending|expected|await/i.test(status);
  const Icon = isResumption ? BellRing : AlertTriangle;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, status);
    } catch {
      /* storage unavailable — the banner simply stays for this page view */
    }
    setDismissed(status);
  };

  return (
    <aside
      className="trading-banner"
      role="region"
      aria-label={copy.aria}
      data-testid="trading-status-banner"
    >
      <div className="u-container trading-banner__inner">
        <span className="trading-banner__icon" aria-hidden="true">
          <Icon size={17} />
        </span>
        <span className="t-micro trading-banner__label">
          {isResumption ? copy.resumption : copy.status}
        </span>
        <p className="trading-banner__text" data-testid="trading-status-banner-text">
          {status}
        </p>{' '}
        <Link
          to="/investor-relations/share-information"
          className="trading-banner__link"
          data-testid="trading-status-banner-link"
        >
          {copy.link}
        </Link>
        <button
          type="button"
          className="trading-banner__dismiss"
          onClick={dismiss}
          aria-label={copy.dismiss}
          data-testid="trading-status-banner-dismiss"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
